import React, { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { SCHEMA_DOMAINS, SCHEMA_LABEL, SCHEMA_VERSION, type SchemaDomain } from "@/data/schema";

/**
 * SchemaExplorer — interactive view of the .person schema v2.0.0,
 * backed by the canonical dataset in `src/data/schema.ts` which mirrors
 * /Docs/02_technical_specification/schema/.
 *
 * Reads the URL hash on mount and on `hashchange` so the ProtocolMap
 * (and any other deep-link source) can open a specific domain by setting
 * the hash to e.g. `#identity`.
 */
const VALID_DOMAIN_IDS = new Set(SCHEMA_DOMAINS.map((d) => d.id));

function readHashDomain(): string | null {
  if (typeof window === "undefined") return null;
  const raw = window.location.hash.replace(/^#/, "");
  return raw && VALID_DOMAIN_IDS.has(raw) ? raw : null;
}

export function SchemaExplorer() {
  const [openId, setOpenId] = useState<string | null>(
    () => readHashDomain() ?? "identity",
  );

  useEffect(() => {
    function sync() {
      const next = readHashDomain();
      if (next) setOpenId(next);
    }
    window.addEventListener("hashchange", sync);
    return () => window.removeEventListener("hashchange", sync);
  }, []);

  return (
    <div data-schema-explorer className="w-full border border-hairline bg-paper-2/30 p-6 rounded-sm">
      <div className="flex flex-wrap justify-between items-baseline gap-3 mb-6 pb-4 border-b border-hairline">
        <h4 className="font-serif text-lg text-ink">{SCHEMA_LABEL}</h4>
        <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-widest text-ink-3">
          <span>v{SCHEMA_VERSION}</span>
          <span className="border-l border-hairline pl-3">{SCHEMA_DOMAINS.length} domains</span>
          <span className="border-l border-hairline pl-3">interactive</span>
        </div>
      </div>
      <ul role="list" className="space-y-px">
        {SCHEMA_DOMAINS.map((d) => (
          <DomainRow
            key={d.id}
            domain={d}
            open={openId === d.id}
            onToggle={() => setOpenId((cur) => (cur === d.id ? null : d.id))}
          />
        ))}
      </ul>
      <p className="mt-6 text-[11px] font-mono text-ink-3 uppercase tracking-widest">
        Every domain except <span className="text-ink">identity</span> and{" "}
        <span className="text-ink">privacy</span> is optional or progressive. A .person at genesis
        may hold only its identity domain and still be a valid .person.
      </p>
    </div>
  );
}

function DomainRow({
  domain,
  open,
  onToggle,
}: {
  domain: SchemaDomain;
  open: boolean;
  onToggle: () => void;
}) {
  const pillColor =
    domain.optionality === "required"
      ? "text-state-active border-state-active/40"
      : domain.optionality === "optional"
      ? "text-ink-2 border-hairline"
      : "text-state-resolved border-state-resolved/40";

  return (
    <li className="border border-hairline bg-paper">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className={cn(
          "w-full text-left px-4 py-3 flex items-center justify-between gap-4 hover:bg-paper-2 transition-colors",
          "focus:outline-none focus:ring-1 focus:ring-ink"
        )}
      >
        <div className="flex items-center gap-4 min-w-0">
          <span className="font-mono text-[10px] text-ink-3 tabular-nums w-6 shrink-0">
            {String(domain.order).padStart(2, "0")}
          </span>
          <span className="font-mono text-sm text-ink truncate">{domain.name}</span>
          <span className="hidden md:inline font-serif text-sm text-ink-2 italic truncate">
            {domain.headline}
          </span>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span
            className={cn(
              "border px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest",
              pillColor
            )}
          >
            {domain.optionality}
          </span>
          <span aria-hidden="true" className="font-mono text-ink-3 text-xs">
            {open ? "[\u2212]" : "[+]"}
          </span>
        </div>
      </button>
      {open && (
        <div className="px-4 pb-4 pt-0 border-t border-hairline bg-paper-2/40">
          <p className="text-sm text-ink-2 font-serif leading-relaxed my-4 max-w-2xl">
            {domain.description}
          </p>
          <ul role="list" className="divide-y divide-hairline border-t border-hairline">
            {domain.fields.map((f) => (
              <li key={f.name} className="py-2 grid grid-cols-1 sm:grid-cols-12 gap-1 sm:gap-4 items-baseline">
                <span className="font-mono text-sm text-ink sm:col-span-3 truncate">
                  {f.name}
                  {f.required && (
                    <span className="ml-2 text-[10px] uppercase text-state-active border border-state-active/40 px-1 tracking-widest">
                      req
                    </span>
                  )}
                </span>
                <span className="font-mono text-xs text-ember sm:col-span-3 truncate">
                  {f.type}
                </span>
                <span className="text-xs text-ink-2 sm:col-span-6 font-sans leading-relaxed">
                  {f.description}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </li>
  );
}
